import * as React from 'react';
import { memo } from 'react';
import { StyleSheet } from 'react-native';

import { Text } from '@/components/TextComponent';
import { AnimatedPressableOpacity } from '@/components/animated-pressable-opacity/AnimatedPressableOpacity';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import { PlayerProfileImage } from '@/components/player-profile/PlayerProfileImage';
import { leagueConfigSelector, useLeagueConfigsStore } from '@/feature/betslip-pickem/hooks/use-league-configs-store';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { useOpenPlayerProfile } from '@/feature/player-profile-sheets/hooks/use-open-player-profile-sheet';
import { getPlayerNameInfo } from '@/feature/player-profile-sheets/utils/player-profile-utils';
import { getPlayerJerseyNumber } from '@/utils/formatPlayerInfo';
import { getPlayerLogoDetails } from '@/utils/get-player-logo-details';
import { useShallow } from 'zustand/react/shallow';

import { FIRST_TOP10_CARD_SIZE, PopularPlayerNumberIcon, TOP10_CARD_SIZE } from './PopularPlayersList';

export const PlayerSmallCard = memo(
    ({
        player,
        showNumber,
        trackEvent,
        listOfPlayers,
        index,
        testID,
    }: {
        player: PlayerWithTeamAndEvent;
        showNumber: boolean;
        trackEvent?: () => void;
        listOfPlayers: PlayerWithTeamAndEvent[];
        index: number;
        testID?: string;
    }) => {
        const event = player.event;
        const team = player.team;
        const league = event?.league ?? team?.league ?? player.league;
        const { leagueIcon, leagueColor } = useLeagueConfigsStore(useShallow(leagueConfigSelector(league)));
        const isFirstItem = index === 0;
        const isLastItem = index === listOfPlayers.length - 1;
        const position = index + 1;
        const hasTwoDigits = position > 9;
        const { logoText, teamLogo } = getPlayerLogoDetails(player, league, team);
        const playerNumber = getPlayerJerseyNumber(league, player?.jerseyNumber);
        const { openPlayerProfile } = useOpenPlayerProfile(listOfPlayers);

        const handleOpenPlayerProfile = () => {
            openPlayerProfile({ playerId: player.id, eventId: event.id });
            trackEvent?.();
        };

        const { mainName } = getPlayerNameInfo(player);

        return (
            <AnimatedPressableOpacity
                onPress={handleOpenPlayerProfile}
                flex={1}
                accessible={false}
                marginLeft={isFirstItem ? 's16' : 's0'}
                marginRight={isLastItem ? 's16' : 's0'}
                testID={testID}
            >
                <Box
                    style={
                        showNumber
                            ? { ...styles.cardContainer, width: isFirstItem ? FIRST_TOP10_CARD_SIZE : TOP10_CARD_SIZE }
                            : undefined
                    }
                >
                    <Box width={72}>
                        <PlayerProfileImage
                            playerImageUrl={player.icon}
                            teamImageUrl={teamLogo ?? leagueIcon}
                            imageVariant={'extra-large'}
                            teamColor={team?.color ?? leagueColor}
                            teamSecondaryColor={team?.secondaryColor}
                            bgColor={ds.colorToken.background.neutral.subtler}
                            logoText={logoText}
                            playerNumber={playerNumber}
                            league={league}
                            playerId={player.id}
                        />
                        <Text
                            numberOfLines={1}
                            textAlign={'center'}
                            variant={'titleSmall'}
                            color={ds.colorToken.text.primary}
                            marginTop={'s10'}
                        >
                            {mainName}
                        </Text>
                    </Box>
                </Box>
                {showNumber ? (
                    <PopularPlayerNumberIcon
                        position={index + 1}
                        isFirstItem={isFirstItem}
                        hasTwoDigits={hasTwoDigits}
                    />
                ) : null}
            </AnimatedPressableOpacity>
        );
    }
);

const styles = StyleSheet.create({
    cardContainer: {
        alignItems: 'flex-end',
    },
});
